import {repoTasksApiPath} from '@github-ui/agent-sessions/utils/api-url-helpers'
import {fetchCAPI} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {ClientTool} from '@github-ui/ahp-relay/relay-connection-runtime'

import {listAgentSessions} from './agent-session-list'
import {buildCreateSessionClientTool, type CreateSessionConfirmation} from './create-session-tool'
import {LIST_SESSIONS_LIMIT, buildListSessionsClientTool} from './session-list-tool'
import {buildViewSessionsClientTool} from './view-sessions-tool'

export interface SessionHandoffToolsConfig {
  /** Copilot API base URL used to list/create cloud agent sessions. */
  apiURL: string
  /**
   * Navigate the browser to a cloud agent session by task id. Supplied by the
   * React host (which owns the router); when omitted the view_sessions tool is
   * not contributed.
   */
  navigateToSession?: (taskId: string) => void
  /**
   * Ask the user to approve starting a cloud agent session before create_session
   * writes anything. Supplied by the React host (which owns the confirm UI);
   * when omitted the write-capable create_session tool is not contributed.
   */
  confirmCreateSession?: (details: CreateSessionConfirmation) => Promise<boolean>
}

/**
 * Build the flag-gated session-handoff client tools (list/create/view cloud
 * agent sessions). Shared by the lazy-provisioning runtime (fresh chat) and the
 * fauxpilot runtime (reattach on reload) so the handlers are registered on the
 * relay in both cases; otherwise the daemon advertises the tool definitions but
 * the client has no fulfilment handler after a full page reload.
 */
export function buildSessionHandoffClientTools(config: SessionHandoffToolsConfig): ClientTool[] {
  // The session-handoff tools (list/create/view cloud agent sessions) are
  // gated behind a feature flag while the feature rolls out.
  if (!isFeatureEnabled('copilot_chat_session_handoff')) return []

  const tools: ClientTool[] = []
  const canOpenViaViewTool = Boolean(config.navigateToSession)
  tools.push(
    buildListSessionsClientTool({
      listSessions: () => listAgentSessions({apiURL: config.apiURL, limit: LIST_SESSIONS_LIMIT}),
      canOpenViaViewTool,
    }),
  )
  if (config.confirmCreateSession) {
    const confirmCreateSession = config.confirmCreateSession
    tools.push(
      buildCreateSessionClientTool({
        confirm: confirmCreateSession,
        createTask: params =>
          fetchCAPI({
            path: repoTasksApiPath(params.nwo),
            method: 'POST',
            copilotApiUrl: config.apiURL,
            body: {
              problem_statement: params.problemStatement,
              event_identifiers: params.eventIdentifiers ?? [],
              ...(params.baseRef && {base_ref: params.baseRef}),
              ...(params.createPullRequest !== undefined && {create_pull_request: params.createPullRequest}),
              ...(params.eventType && {event_type: params.eventType}),
              ...(params.eventContent && {event_content: params.eventContent}),
            },
          }),
      }),
    )
  }
  if (config.navigateToSession) {
    tools.push(buildViewSessionsClientTool({navigateToSession: config.navigateToSession}))
  }
  return tools
}
